import { useMutation } from "@tanstack/react-query";
import {
	ConfirmAssetDocument,
	DeleteAssetDocument,
	GetAssetDownloadUrlDocument,
	RegisterAssetDocument,
	type RegisterAssetInput,
} from "@/common/artemis-sdk/graphql";
import { useGqlClient } from "../use-gql-client";

/** Registers a new asset and returns the pending record and a pre-signed staging upload URL. */
export function useRegisterAsset() {
	const gqlFetch = useGqlClient();

	return useMutation({
		mutationFn: async (input: RegisterAssetInput) => {
			const data = await gqlFetch(RegisterAssetDocument, { input });
			return data.registerAsset;
		},
	});
}

/** Confirms a pending asset upload, copying from staging to live and activating the record. */
export function useConfirmAsset() {
	const gqlFetch = useGqlClient();

	return useMutation({
		mutationFn: async (assetId: string) => {
			const data = await gqlFetch(ConfirmAssetDocument, { assetId });
			return data.confirmAsset.asset;
		},
	});
}

export function useDeleteAssets() {
	const gqlFetch = useGqlClient();

	return useMutation({
		mutationFn: async (assetIds: string[]) => {
			await Promise.all(
				assetIds.map((assetId) => gqlFetch(DeleteAssetDocument, { assetId })),
			);
		},
	});
}

/** Returns a pre-signed download URL for the specified asset. */
export function useGetAssetDownloadUrl() {
	const gqlFetch = useGqlClient();

	return useMutation({
		mutationFn: async (assetId: string) => {
			const data = await gqlFetch(GetAssetDownloadUrlDocument, { assetId });
			return data.assetDownloadUrl as string;
		},
	});
}
